// useMcpPrefs.ts — the caller's MCP server enable-set, for the Tools modal.
//
// The tool-calling loop itself lives server-side; this is configuration
// only. `servers` is the cluster-wide MCPServer catalog and `enabled` the
// caller's subset, persisted via /api/me/mcp-prefs. The modal edits a
// draft copy, so Cancel leaves the stored set untouched.
//
// Load failures are non-fatal: an empty catalog just means the modal
// shows "no MCP servers registered" and chat keeps working without tools.
import { useCallback, useEffect, useState } from 'react';
import { mcpApi, mcpPrefsApi } from '../api';
import type { MCPServer } from '../types';
import { toggleInSet } from './sessionRows';

export interface McpPrefs {
  servers: MCPServer[];
  enabled: string[];
  modalOpen: boolean;
  draft: Set<string>;
  saving: boolean;
  openModal: () => void;
  closeModal: () => void;
  toggleDraft: (id: string) => void;
  save: () => Promise<void>;
}

export function useMcpPrefs(): McpPrefs {
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [enabled, setEnabled] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([mcpApi.list().catch(() => [] as MCPServer[]), mcpPrefsApi.get().catch(() => [] as string[])])
      .then(([srvs, en]) => {
        if (!alive) return;
        setServers(srvs || []);
        setEnabled(en || []);
      });
    return () => { alive = false; };
  }, []);

  const openModal = useCallback(() => {
    setDraft(new Set(enabled));
    setModalOpen(true);
  }, [enabled]);

  const save = async () => {
    setSaving(true);
    try {
      const ids = Array.from(draft);
      console.debug('[sessions] saving MCP prefs', { enabled: ids.length });
      const persisted = await mcpPrefsApi.set(ids);
      setEnabled(persisted);
      setModalOpen(false);
    } catch (err) {
      console.error('Failed to save MCP prefs:', err);
    } finally {
      setSaving(false);
    }
  };

  return {
    servers,
    enabled,
    modalOpen,
    draft,
    saving,
    openModal,
    closeModal: () => setModalOpen(false),
    toggleDraft: (id) => setDraft((prev) => toggleInSet(prev, id)),
    save,
  };
}
